import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useSettingsStore } from '@/stores/settingsStore';
import { useAuthStore } from '@/stores/authStore';
import AdminLayout from '@/layouts/AdminLayout';
import CafeLayout from '@/layouts/CafeLayout';

// Admin Pages
import LandingPage from '@/pages/landing';
import LoginPage from '@/pages/auth/LoginPage';
import Dashboard from '@/pages/dashboard';
import OrdersPage from '@/pages/orders';
import UsersPage from '@/pages/users';
import DriversPage from '@/pages/drivers';
import CafesPage from '@/pages/cafes';
import CategoriesPage from '@/pages/categories';
import ReportsPage from '@/pages/reports';
import SettingsPage from '@/pages/settings';

// Cafe Pages
import CafeDashboard from '@/pages/cafe/dashboard';
import CafeOrders from '@/pages/cafe/orders';
import CafeMenu from '@/pages/cafe/menu';
import CafeReviews from '@/pages/cafe/reviews';
import CafeSettings from '@/pages/cafe/settings';

// Protected Route
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

function App() {
  const { theme, language } = useSettingsStore();

  // Apply settings on mount
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [theme, language]);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        
        {/* Protected Dashboard Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="drivers" element={<DriversPage />} />
          <Route path="cafes" element={<CafesPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Protected Cafe Dashboard Routes */}
        <Route
          path="/cafe"
          element={
            <ProtectedRoute>
              <CafeLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<CafeDashboard />} />
          <Route path="orders" element={<CafeOrders />} />
          <Route path="menu" element={<CafeMenu />} />
          <Route path="reviews" element={<CafeReviews />} />
          <Route path="settings" element={<CafeSettings />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
